import * as Sentry from "@sentry/vue";

import { isGoogleMapsModuleLoadFailure } from "~/utilities/helpers/sentryNoise";


export default defineNuxtPlugin({
  name: "sentry",
  enforce: "post",
  async setup(nuxtApp) {
    const currentHost = window.location.hostname; 
    if (currentHost !== "localhost") {
      const config = useRuntimeConfig();
      Sentry.init({
        app: nuxtApp.vueApp,
        autoSessionTracking: true,
        dsn: config.public.SENTRY_API_KEY,
        tunnel: "/api/sentry-tunnel",
        environment:
          currentHost === "rwa-estate.com" ? "production" : "development",
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracePropagationTargets: [
          "https://stage.rwa-estate.com",
          "https://www.rwa-estate.com",
          /^\/api\//,
        ],
        hooks: ["activate", "create", "destroy", "mount", "update"],
        tracesSampleRate: 1.0,
        // This plugin is the only place Sentry is initialised (`@sentry/vue`; the project has no `@sentry/nuxt`
        // module). The filters below used to sit in `sentry.client.config.ts`, which nothing loads, so they never
        // applied (RE-2497).
        //
        // In-app browsers (Instagram / Facebook WebView, etc.) inject their own scripts — e.g.
        // `iabjs://navigation_performance_logger_android` — which throw "Error invoking <method>: Java object is gone"
        // once the native WebView <-> JS bridge is torn down on navigation/unload. Host-app noise (RE-880, RE-881).
        denyUrls: [/iabjs:\/\//],
        ignoreErrors: [
          /Java object is gone/,
          // iOS counterpart: Meta's IAB unified bridge (`IABMV`) reads `window.webkit.messageHandlers`, which is
          // undefined on some WebView states. Our code never touches `window.webkit` (RE-1303).
          /window\.webkit\.messageHandlers/,
        ],
        // Pageloads cancelled because the tab was backgrounded mid-load (`document.hidden`) report inflated
        // web-vitals — the in-app browsers background the WebView almost immediately. Only completed loads feed
        // performance monitoring (RE-1356).
        beforeSendTransaction(event) {
          const trace = event.contexts?.trace;
          const isPageLoad = trace?.op === "pageload" || trace?.op === "navigation";
          const wasCancelled = trace?.status === "cancelled" || Boolean(trace?.data?.["sentry.cancellation_reason"]);

          return isPageLoad && wasCancelled ? null : event;
        },
        beforeSend(event, hint) {
          // Google Maps failing to fetch one of its own parts on the visitor's network (RE-2497) — see sentryNoise.ts.
          if (isGoogleMapsModuleLoadFailure(event)) {
            return null;
          }

          if (event.exception) {
            console.error(
              `[Exeption handled by Sentry]: (${hint.originalException})`,
              {
                event,
                hint,
              }
            );
          }
          return event;
        },
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  },

  env: {
    islands: true,
  },
});
